import { useEffect, useState } from "react";
import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Check, ExternalLink, Code2, Award } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PUBLIC_SITE = "https://wttq.org";

const BADGE_URLS = {
  gold: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/3qf4e4f0_Black%20And%20White%20Simple%20Icon%20Logo.png",
  silver: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/80ndwcjh_Black%20And%20White%20Simple%20Icon%20Logo%20%281%29.png",
  bronze: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/o5vrg8qt_Black%20And%20White%20Simple%20Icon%20Logo%20%282%29.png"
};

const SIZES = {
  small: { label: "Small", px: 80 },
  medium: { label: "Medium", px: 120 },
  large: { label: "Large", px: 180 },
};

const ratingLabel = (r) => r ? r.charAt(0).toUpperCase() + r.slice(1) : "";

const BadgeEmbedPage = () => {
  const { type, id } = useParams();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [size, setSize] = useState("medium");
  const [copied, setCopied] = useState(null);

  const isHotel = type === "hotel";
  const endpoint = isHotel ? "hotels" : type === "airline" ? "airlines" : "destinations";

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const res = await axios.get(`${API}/${endpoint}/${id}`);
        setItem(res.data);
        // Log view event (fire-and-forget)
        axios.post(`${API}/badge-events`, {
          listing_type: type,
          listing_id: id,
          event_type: "view",
        }).catch(() => {});
      } catch (err) {
        setError("Listing not found");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id, endpoint, type]);

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(null), 2000);
      // Log copy event (fire-and-forget)
      const eventType = {
        html: "copy_html",
        markdown: "copy_markdown",
        image: "copy_image",
        profile: "copy_profile",
      }[key];
      if (eventType) {
        axios.post(`${API}/badge-events`, {
          listing_type: type,
          listing_id: id,
          event_type: eventType,
          size,
        }).catch(() => {});
      }
    }).catch(() => toast.error("Failed to copy"));
  };

  // Auth gate — only approved partner users can access the embed page
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  }

  if (error || !item) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA]">
        <h1 className="font-heading text-3xl font-bold text-[#0B132B] mb-4">Listing Not Found</h1>
        <Link 
          to="/#directory" 
          className="text-[#D4AF37] hover:text-[#92710d] flex items-center gap-2"
          data-testid="back-to-directory-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Directory
        </Link>
      </div>
    );
  }

  const rating = item.rating;
  const badgeUrl = BADGE_URLS[rating];
  const profileUrl = `${PUBLIC_SITE}/${type}/${id}`;
  const displayName = item.name;
  const altText = `${displayName} — WTTQ ${ratingLabel(rating)} Certified`;
  const px = SIZES[size].px;

  const htmlSnippet = `<a href="${profileUrl}" target="_blank" rel="noopener" title="${altText}">
  <img src="${badgeUrl}" alt="${altText}" width="${px}" height="${px}" style="max-width:${px}px;height:auto;border:0;" />
</a>`;

  const markdownSnippet = `[![${altText}](${badgeUrl})](${profileUrl})`;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="badge-embed-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link 
            to={`/${type}/${id}`}
            className="inline-flex items-center gap-2 text-[#4A5568] hover:text-[#0B132B] transition-colors"
            data-testid="embed-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to {type.charAt(0).toUpperCase() + type.slice(1)}</span>
          </Link>
          <Link to="/" className="font-heading text-xl font-bold text-[#0B132B]">
            WTTQ<span className="text-[#D4AF37]">.org</span>
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FDF9EC] border border-[#E8D38E] text-[#92710d] text-xs font-semibold uppercase tracking-wider mb-4">
            <Award className="w-3.5 h-3.5" />
            Certified Badge Embed
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#0B132B] mb-3" data-testid="embed-page-title">
            Share Your WTTQ Certification
          </h1>
          <p className="text-[#4A5568] max-w-2xl mx-auto">
            Display your <strong>{ratingLabel(rating)} Quality</strong> certification on your website, email signature, or
            marketing materials. Copy-paste the snippet below — it links visitors back to your verified WTTQ profile.
          </p>
        </div>

        {/* Listing Info */}
        <div className="bg-white border border-gray-100 p-6 mb-8 flex items-center gap-6" data-testid="embed-listing-info">
          <img src={badgeUrl} alt={altText} className="w-20 h-20 object-contain flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-[#718096] mb-1">
              {isHotel ? "Quality Hotel" : type === "airline" ? "Quality Airline" : "Quality Destination"} · {ratingLabel(rating)} Tier
            </p>
            <h2 className="font-heading text-2xl font-bold text-[#0B132B] truncate">{displayName}</h2>
            <a 
              href={profileUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[#D4AF37] hover:text-[#92710d] mt-1"
              data-testid="embed-profile-link"
            >
              View public profile <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* Size Selector */}
        <div className="mb-8">
          <label className="block text-sm font-semibold text-[#0B132B] mb-3">Badge Size</label>
          <div className="inline-flex rounded-full border border-gray-200 bg-white p-1" data-testid="embed-size-selector">
            {Object.entries(SIZES).map(([key, cfg]) => (
              <button
                key={key}
                onClick={() => setSize(key)}
                className={`px-5 py-2 text-sm font-medium rounded-full transition-colors ${
                  size === key 
                    ? "bg-[#0B132B] text-white" 
                    : "text-[#4A5568] hover:text-[#0B132B]"
                }`}
                data-testid={`embed-size-${key}`}
              >
                {cfg.label} ({cfg.px}px)
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Preview */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-semibold text-[#0B132B] mb-3 uppercase tracking-wider">Live Preview</h3>
            <div 
              className="bg-white border border-gray-100 p-10 flex items-center justify-center min-h-[320px]"
              data-testid="embed-preview"
            >
              <a 
                href={profileUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                title={altText}
                className="inline-block transition-transform hover:scale-105"
              >
                <img 
                  src={badgeUrl} 
                  alt={altText}
                  style={{ width: `${px}px`, height: `${px}px`, maxWidth: `${px}px` }}
                  className="object-contain drop-shadow-md"
                />
              </a>
            </div>
            <p className="text-xs text-[#718096] mt-3 text-center">
              Clicking the badge opens <code className="bg-gray-100 px-1.5 py-0.5 rounded">{profileUrl.replace(/^https?:\/\//, "")}</code>
            </p>
          </div>

          {/* Snippets */}
          <div className="lg:col-span-3 space-y-6">
            {/* HTML */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Code2 className="w-4 h-4 text-[#0B132B]" />
                  <h3 className="text-sm font-semibold text-[#0B132B] uppercase tracking-wider">HTML</h3>
                </div>
                <button
                  onClick={() => copyToClipboard(htmlSnippet, "html")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#0B132B] bg-white border border-gray-200 hover:border-[#D4AF37] hover:text-[#92710d] rounded-full transition-colors"
                  data-testid="copy-html-button"
                >
                  {copied === "html" ? <><Check className="w-3.5 h-3.5" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
                </button>
              </div>
              <pre className="bg-[#0B132B] text-[#E2E8F0] p-4 rounded-md overflow-x-auto text-xs leading-relaxed" data-testid="html-snippet">
                <code>{htmlSnippet}</code>
              </pre>
              <p className="text-xs text-[#718096] mt-2">
                Use this on your website, blog, or any page that accepts HTML.
              </p>
            </div>

            {/* Markdown */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Code2 className="w-4 h-4 text-[#0B132B]" />
                  <h3 className="text-sm font-semibold text-[#0B132B] uppercase tracking-wider">Markdown</h3>
                </div>
                <button
                  onClick={() => copyToClipboard(markdownSnippet, "markdown")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#0B132B] bg-white border border-gray-200 hover:border-[#D4AF37] hover:text-[#92710d] rounded-full transition-colors"
                  data-testid="copy-markdown-button"
                >
                  {copied === "markdown" ? <><Check className="w-3.5 h-3.5" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
                </button>
              </div>
              <pre className="bg-[#0B132B] text-[#E2E8F0] p-4 rounded-md overflow-x-auto text-xs leading-relaxed" data-testid="markdown-snippet">
                <code>{markdownSnippet}</code>
              </pre>
              <p className="text-xs text-[#718096] mt-2">
                For README files, GitHub, Notion, or other Markdown-based platforms.
              </p>
            </div>

            {/* Direct image URL */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-[#0B132B]" />
                  <h3 className="text-sm font-semibold text-[#0B132B] uppercase tracking-wider">Direct Image URL</h3>
                </div>
                <button
                  onClick={() => copyToClipboard(badgeUrl, "image")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#0B132B] bg-white border border-gray-200 hover:border-[#D4AF37] hover:text-[#92710d] rounded-full transition-colors"
                  data-testid="copy-image-url-button"
                >
                  {copied === "image" ? <><Check className="w-3.5 h-3.5" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
                </button>
              </div>
              <pre className="bg-[#0B132B] text-[#E2E8F0] p-4 rounded-md overflow-x-auto text-xs leading-relaxed" data-testid="image-url-snippet">
                <code>{badgeUrl}</code>
              </pre>
              <p className="text-xs text-[#718096] mt-2">
                Useful for email signatures, presentations, or any platform accepting a raw image URL.
              </p>
            </div>

            {/* Profile URL */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-[#0B132B]" />
                  <h3 className="text-sm font-semibold text-[#0B132B] uppercase tracking-wider">Verification Link</h3>
                </div>
                <button
                  onClick={() => copyToClipboard(profileUrl, "profile")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#0B132B] bg-white border border-gray-200 hover:border-[#D4AF37] hover:text-[#92710d] rounded-full transition-colors"
                  data-testid="copy-profile-url-button"
                >
                  {copied === "profile" ? <><Check className="w-3.5 h-3.5" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
                </button>
              </div>
              <pre className="bg-[#0B132B] text-[#E2E8F0] p-4 rounded-md overflow-x-auto text-xs leading-relaxed" data-testid="profile-url-snippet">
                <code>{profileUrl}</code>
              </pre>
            </div>
          </div>
        </div>

        {/* Guidelines */}
        <div className="mt-12 bg-white border border-gray-100 p-8">
          <h3 className="font-heading text-xl font-bold text-[#0B132B] mb-4">Usage Guidelines</h3>
          <ul className="space-y-3 text-[#4A5568] text-sm leading-relaxed">
            <li className="flex gap-3">
              <span className="text-[#D4AF37] font-bold">·</span>
              <span>The badge must link back to your official WTTQ profile at <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{profileUrl}</code>.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-[#D4AF37] font-bold">·</span>
              <span>Do not alter, crop, recolour, or stretch the badge artwork. You may adjust size uniformly.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-[#D4AF37] font-bold">·</span>
              <span>Use the badge only while your {ratingLabel(rating)} certification is valid.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-[#D4AF37] font-bold">·</span>
              <span>For press, print, or high-resolution versions, please contact WTTQ.org directly.</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0B132B] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/50 text-sm">
            &copy; {new Date().getFullYear()} WTTQ.Org. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BadgeEmbedPage;
